import React, { Component } from 'react'
import { CoremediaRenderer } from '@ycos/coremedia-renderer'
import getConfig from 'next/config'
import InjectPropertiesRule from '@ycos/coremedia-renderer/src/rules/InjectPropertiesRule'
import LocaliseExternalLinksRule from '@ycos/coremedia-renderer/src/rules/LocaliseExternalLinksRule'
import cmPreviewRule from '@ycos/coremedia-renderer/src/rules/CMPreviewRule'
import { OptimizelyProvider, createInstance } from '@ynap/optimizely-utils'
import CoremediaPageContext from '@ycos/coremedia-page-context'
import withRouter from '../withRouter'
import HelmetHead from './HelmetHead'
import getOptimizely from '../utils/getOptimizely'

const CoremediaPage = (options) => {
  return withRouter(
    class CoremediaPage extends Component {
      static async getInitialProps(requestContext) {
        // in safari the popstate event is fired when user press back and
        // causes the getInitialProps to be called in the SSR version
        // This will pickup the current props and return it as a workaround
        // https://github.com/zeit/next.js/issues/2360
        if (typeof requestContext.res === 'undefined') {
          const nextData = __NEXT_DATA__.props // eslint-disable-line
          return 'pageProps' in nextData ? nextData.pageProps : nextData
        }

        const { req, res, query } = requestContext
        const { nativeapp } = req.cookies
        const { brand, seoConfig, contentService, optimizely: optimizelyConfig } = getConfig().publicRuntimeConfig
        const { locale, originalUrl, forceTrailingSlashes } = req
        const { page, useDebugRenderingTools, products, contactDetails } = res.locals

        const { getAdditionalProps, mockData } = options
        if (!req.optimizely?.datafile) {
          // eslint-disable-next-line
          console.log('req.optimizely?.datafile undefined')
        }
        const ssrOptimizely = req.optimizely?.datafile
          ? createInstance({
            sdkKey: undefined,
            datafile: req.optimizely?.datafile, // Ensure the SDK doesn't also try to auto-update on its own
            datafileOptions: {
              autoUpdate: false // Ensure the SDK doesn't also try to auto-update on its own
            }
          })
          : undefined
        const props = {
          nativeapp: nativeapp ? nativeapp.toLowerCase() : undefined,
          locale,
          webview: !!nativeapp || query.webview === 'true' || query.bare === 'true',
          isPreview: contentService.isPreview,
          page: mockData || page,
          originalUrl,
          seoConfig,
          useDebugRenderingTools,
          rulesConfig: contentService.rules,
          brand,
          products,
          forceTrailingSlashes,
          ssrOptimizely,
          optimizelyConfig,
          contactDetails
        }
        const additionalProps = typeof getAdditionalProps === 'function' ? getAdditionalProps({ req, res, query }) : {}

        if (typeof additionalProps.then !== 'undefined') {
          return additionalProps.then((data) => {
            return {
              ...props,
              ...data
            }
          })
        }

        return {
          ...props,
          ...additionalProps
        }
      }

      render() {
        const {
          brand,
          page,
          useDebugRenderingTools,
          webview,
          locale,
          rulesConfig,
          products,
          nativeapp,
          isPreview,
          ssrOptimizely,
          optimizelyConfig,
          optimizelyState,
          contactDetails
        } = this.props

        const Layout = options.layout
        const rules = (typeof options.rules === 'function' ? options.rules(this.props) : options.rules) || []
        const components = (typeof options.components === 'function' ? options.components(this.props) : options.components) || []
        if (webview) {
          rules.push(InjectPropertiesRule({ webview }))
        }

        if (rulesConfig && locale && rulesConfig.localiseExternalLinks) {
          rules.push(LocaliseExternalLinksRule(brand.domain, locale.id))
        }

        if (isPreview) rules.push(cmPreviewRule)

        const context = {
          nativeapp,
          webview,
          locale,
          products
        }

        const ssr = typeof window === 'undefined'
        const optimizely = ssr ? ssrOptimizely : getOptimizely(optimizelyConfig?.sdkKey, window[optimizelyState]?.datafile)

        return (
          <OptimizelyProvider optimizely={optimizely} isServerSide={ssr}>
            <CoremediaPageContext.Provider value={context}>
              <Layout pageTitle={page?.teaser?.teaserTitle || page.teaserTitle} pageKey={page.pageKey} tags={page.tags} locale={locale} content={page.content} nativeapp={nativeapp} contactDetails={contactDetails}>
                <HelmetHead {...page} />
                {CoremediaRenderer(page, components, {
                  ToolingComponents: options.tooling || {},
                  rules,
                  useDebugRenderingTools
                })}
              </Layout>
            </CoremediaPageContext.Provider>
          </OptimizelyProvider>
        )
      }
    }
  )
}

export default CoremediaPage
